import React from "react";
import {
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  Divider,
  useTheme,
  ButtonBase,
  Tooltip,
} from "@mui/material";
import { motion, LayoutGroup } from "framer-motion";
import LogoutIcon from "@mui/icons-material/Logout";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// --- Visual Constants ---
const HEADER_HEIGHT = 64;
const GLASS_BG = "rgba(255, 255, 255, 0.85)"; 
const GLASS_BLUR = "16px"; 

export default function HeaderBar({
  THEME_CONFIGS,
  selectedSection,
  setThemeInUrl,
  user,
  isDemoUser = false,
  onLogout,
  clientIsAllowed,
  onTourRestart,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [menuEl, setMenuEl] = React.useState(null);
  const DISPLAY_COMPANY_ALIASES = {
    "butlins": "Butlin's",
  };
  const displayCompanyName =
    DISPLAY_COMPANY_ALIASES[String(user?.company || "").trim().toLowerCase()] ||
    user?.company;

  // --- Menu Logic ---
  const openMenu = (e) => setMenuEl(e.currentTarget);
  const closeMenu = () => setMenuEl(null);

  const handleLogout = () => {
    axios
      .post("/api/user/logout/")
      .then(() => {
        if (typeof onLogout === "function") onLogout();
        window.location.href = "/";
      })
      .catch((e) => console.error("Logout error", e));
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("")
    : "U";
  
  const showDemoPill = isDemoUser;
  const demoVariant = String(user?.demo_variant || "default").trim().toLowerCase();
  const demoCanAccessClient = !isDemoUser || demoVariant !== "default";
  const clientTabLabel = isDemoUser
    ? (demoVariant === "holiday-tax-demo" ? "Holiday Tax" : "Demo")
    : (displayCompanyName || "Client");


  return (
    <Box
      component="header"
      // REMOVED: initial/animate props. 
      // This ensures the bar is visible instantly, stopping the "flash" on mount.
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        width: "100%",
        height: HEADER_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, md: 4 },
        transition: "all 0.2s ease",

        // --- Professional Glass Effect ---
        backgroundColor: GLASS_BG,
        backdropFilter: `blur(${GLASS_BLUR})`,
        WebkitBackdropFilter: `blur(${GLASS_BLUR})`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "none", // Clean, flat look
      }}
    >
      {/* 1. Brand / Logo */}
      {/* 1. Brand / Logo */}
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 150, gap: 1 }}>
        <motion.img 
          src={process.env.PUBLIC_URL + "/static/images/constituency2.png"}
          alt="Logo"
          style={{ height: 34, width: "auto", objectFit: "contain" }}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        />

        {onTourRestart && (
          <Tooltip title="Replay guided tour" placement="bottom">
            <IconButton
              component={motion.button}
              onClick={onTourRestart}
              whileHover={{ scale: 1.15, rotate: 15 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              size="small"
              sx={{
                width: 28, height: 28, ml: 0.5,
                color: "rgba(0,0,0,0.35)",
                border: "1px solid rgba(0,0,0,0.1)",
                bgcolor: "rgba(0,0,0,0.03)",
                "&:hover": { color: "#0d9488", borderColor: "rgba(13,148,136,0.35)", bgcolor: "rgba(13,148,136,0.06)" },
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
            </IconButton>
          </Tooltip>
        )}

        {showDemoPill && (
          <Box
            sx={{
              px: 1.25,
              py: 0.35,
              borderRadius: 99,
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "0.02em",
              color: theme.palette.text.primary,
              backgroundColor: "rgba(137, 242, 88, 0.25)",
              border: "1px solid rgba(0,0,0,0.08)",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            Demo Version
          </Box>
        )}
      </Box>


      {/* 2. Navigation Tabs (Center) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
        <LayoutGroup id="header-tabs-group">
          {Object.keys(THEME_CONFIGS).map((key) => {
            if (key === "client" && (!clientIsAllowed() || !demoCanAccessClient)) return null;
            if (key === "report") return null;

            const isSelected = selectedSection === key;
            const label =
              key === "client"
                ? clientTabLabel
                : THEME_CONFIGS[key].label;

            return (
              <ButtonBase
                key={key}
                id={key === "report" ? "tour-report-tab" : undefined}
                disableRipple
                onClick={() => setThemeInUrl(key)}
                sx={{
                  position: "relative",
                  display: "flex", alignItems: "center", gap: 0.7,
                  px: 2, py: 1.5,
                  fontSize: "0.875rem",
                  fontWeight: isSelected ? 700 : 500,
                  fontFamily: "'DM Sans', sans-serif",
                  color: isSelected ? "#0f9fab" : "rgba(15,23,42,0.5)",
                  transition: "color 0.18s ease, background 0.18s ease",
                  borderRadius: "8px",
                  "&:hover": {
                    color: isSelected ? "#0f9fab" : "rgba(15,23,42,0.75)",
                    bgcolor: isSelected ? "transparent" : "rgba(15,23,42,0.04)",
                  },
                }}
              >
                <span style={{ fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
                {/* Underline */}
                {isSelected && (
                  <Box
                    component={motion.div}
                    layoutId="active-underline"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    sx={{
                      position: "absolute", bottom: 0, left: 8, right: 8,
                      height: "2px", borderRadius: "2px",
                      bgcolor: "#0f9fab", opacity: 0.7,
                    }}
                  />
                )}
              </ButtonBase>
            );
          })}
        </LayoutGroup>

        {/* Report — separated from nav tabs */}
        {"report" in THEME_CONFIGS && (
          <>
            <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: "center", mx: 1, opacity: 0.3 }} />
            <ButtonBase
              id="tour-report-tab"
              disableRipple
              component={motion.button}
              onClick={() => setThemeInUrl("report")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              sx={{
                display: "flex", alignItems: "center", gap: "6px",
                px: 1.75, py: 0.65,
                borderRadius: "8px",
                fontSize: "0.82rem", fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
                color: selectedSection === "report" ? "#0f9fab" : "rgba(15,23,42,0.5)",
                backgroundColor: selectedSection === "report" ? "#e8fbfb" : "transparent",
                border: `1px solid ${selectedSection === "report" ? "rgba(15,159,171,0.28)" : "rgba(15,23,42,0.1)"}`,
                transition: "all 0.18s ease",
                "&:hover": {
                  color: "#0f9fab",
                  backgroundColor: "#e8fbfb",
                  borderColor: "rgba(15,159,171,0.28)",
                },
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              Build Report
            </ButtonBase>
          </>
        )}

        {/* Q2 Highlights — Stack users only */}
        {user?.company_slug === "stack-data-strategy" && <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: "center", mx: 1, opacity: 0.3 }} />}
        {user?.company_slug === "stack-data-strategy" && <Tooltip title="Q2 2026 highlights — what moved this quarter" placement="bottom">
          <ButtonBase
            disableRipple
            component={motion.button}
            onClick={() => navigate("/highlights/q2-2026")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            sx={{
              display: "flex", alignItems: "center", gap: "6px",
              px: 1.75, py: 0.65,
              borderRadius: "8px",
              fontSize: "0.82rem", fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif",
              color: "#0d9488",
              backgroundColor: "rgba(13,148,136,0.07)",
              border: "1px solid rgba(13,148,136,0.2)",
              transition: "all 0.18s ease",
              "&:hover": {
                backgroundColor: "rgba(13,148,136,0.13)",
                borderColor: "rgba(13,148,136,0.35)",
              },
            }}
          >
            {/* Sparkle icon */}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/>
            </svg>
            Q2 Highlights
          </ButtonBase>
        </Tooltip>}
      </Box>

      {/* 3. User Profile */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", minWidth: 150, gap: 1 }}>

        <IconButton
          onClick={openMenu}
          size="small"
          sx={{
            p: 0.5,
            transition: "all 0.2s",
            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            ...(Boolean(menuEl) && { bgcolor: "rgba(0,0,0,0.05)" }),
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "#0f9fab",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={closeMenu}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                backdropFilter: "blur(10px)",
                bgcolor: "rgba(255,255,255,0.9)",
              },
            },
          }}
        >
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {user?.name || "User"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {displayCompanyName || "Tracking Data"}
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          {/* View mobile site — clears preferDesktop so auto-redirect resumes, then navigates to /m */}
          <MenuItem
            onClick={() => { localStorage.removeItem("preferDesktop"); window.location.href = "/m"; }}
            sx={{ py: 1.5, px: 2.5 }}
          >
            <ListItemIcon>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              View mobile site
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 2.5 }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
            </ListItemIcon>
            <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
              Log out
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
